import { reactive, type ComputedRef, type Ref, type UnwrapNestedRefs } from 'vue';
import type { IMoviePlayerControlArea, IMoviePlayerVideo } from 'src/models';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import {
  useMoviePlayerPlaybackCore,
  type PlaybackCoreContext,
} from 'src/components/lib/MoviePlayer/composables/useMoviePlayerPlaybackCore';
import { useMoviePlayerVideoIds } from 'src/components/lib/MoviePlayer/composables/useMoviePlayerVideoIds';

interface Deps {
  refMoviePlayerVideos: Ref<IMoviePlayerVideo[] | undefined>;
  refControlArea: Ref<IMoviePlayerControlArea | undefined>;
}

type BasicPlayerPlaybackContext = PlaybackCoreContext & {
  vid1: ComputedRef<string>;
  vid2: ComputedRef<string>;
  vid3: ComputedRef<string>;
};

// 再生コア（play/pause/tick/speed/volume/シーク/video.jsイベント/mgi管理）を切り出したcomposable。
// BasicMoviePlayerから利用する。
export function useBasicPlayerPlayback(
  state: UnwrapNestedRefs<MoviePlayerState>,
  deps: Deps,
): UnwrapNestedRefs<BasicPlayerPlaybackContext> {
  // as constで各要素をリテラル型として固定し、useMoviePlayerVideoIdsの戻り値がvid1/vid2/vid3の
  // 各プロパティを持つRecordと推論されるようにする（asなしだとstring[]に広がり分割代入できない）。
  const { vid1, vid2, vid3 } = useMoviePlayerVideoIds(['vid1', 'vid2', 'vid3'] as const);

  const core = useMoviePlayerPlaybackCore(state, {
    refControlArea: deps.refControlArea,
    prepareVideoIds: () => [vid1.value, vid2.value, vid3.value],
    loadVideoIds: () => [vid2.value, vid1.value, vid3.value],
    prepareVideoRefs: () => {
      if (!deps.refMoviePlayerVideos.value) return;

      deps.refMoviePlayerVideos.value.forEach(e => {
        e.prepareVideojs();
      });
    },
    changeVideoPlayMode: (isPlayBackward) => {
      if (!deps.refMoviePlayerVideos.value) return;

      deps.refMoviePlayerVideos.value.forEach(e => {
        e.changeVjsPlayMode(isPlayBackward);
      });
    },
  });

  return reactive({
    vid1,
    vid2,
    vid3,
    ...core,
  });
}
